/**
 * TicketQuickCreateDialog - Minimal dialog for quick ticket creation
 *
 * Following the Quick Create Dialog pattern:
 * - 3-5 fields maximum (Subject, Contact, Priority, Description)
 * - Redirects to detail page after creation for full editing
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { cn } from '../../../lib/utils';
import { isRichTextEmpty } from '../../../shared/utils/htmlToText';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { DialogForm, CompactRichTextEditor } from '../../../shared/components/common';
import { useCreateTicket } from '../hooks/useTickets';
import { OrganizationContactComboSelect } from '../../master_data/contacts/components/OrganizationContactComboSelect';
import { ProductReferencePicker } from '../../master_data/products/components/ProductReferencePicker';
import { productReferenceFromFields } from '../../master_data/products/types';
import type { Ticket, TicketCreate, TicketPriority } from '../types';
import { toast } from 'sonner';
import { useApiError } from '../../../shared/hooks/useApiError';

interface TicketQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  /** Pre-fill contact from context */
  defaultContactId?: string;
  /** Pre-fill company from context */
  defaultCompanyId?: string;
  /** Pre-fill product from context */
  defaultProductId?: string;
  /** Callback when ticket is created (before navigation) */
  onCreated?: (ticket: Ticket) => void;
  /** If true, don't navigate to detail page after creation */
  skipNavigation?: boolean;
}

interface FormData {
  subject: string;
  description: string;
  contactId: string;
  organizationId: string;
  productId: string;
  priority: TicketPriority;
}

export function TicketQuickCreateDialog({
  open,
  onClose,
  defaultContactId,
  defaultCompanyId,
  defaultProductId,
  onCreated,
  skipNavigation = false,
}: TicketQuickCreateDialogProps) {
  const { t } = useTranslation('support');
  const navigate = useNavigate();

  const createMutation = useCreateTicket();
  const { handleMutationError } = useApiError();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    setError,
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      subject: '',
      description: '',
      contactId: defaultContactId || '',
      organizationId: defaultCompanyId || '',
      productId: defaultProductId || '',
      priority: 'medium',
    },
  });

  const contactId = watch('contactId');
  const organizationId = watch('organizationId');
  const productId = watch('productId');

  useEffect(() => {
    if (open) {
      reset({
        subject: '',
        description: '',
        contactId: defaultContactId || '',
        organizationId: defaultCompanyId || '',
        productId: defaultProductId || '',
        priority: 'medium',
      });
    }
  }, [open, defaultContactId, defaultCompanyId, defaultProductId, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload: TicketCreate = {
        subject: data.subject,
        description: isRichTextEmpty(data.description) ? undefined : data.description,
        contact_id: data.contactId,
        organization_id: data.organizationId || undefined,
        product_id: data.productId || undefined,
        priority: data.priority,
        channel: 'phone', // Default channel for quick create
      };
      const newTicket = await createMutation.mutateAsync(payload);

      toast.success(t('tickets.created'));
      onCreated?.(newTicket);
      onClose();

      if (!skipNavigation) {
        navigate(`/support/tickets/${newTicket.id}`);
      }
    } catch (error) {
      handleMutationError(error, setError);
    }
  };

  return (
    <DialogForm
      open={open}
      onClose={onClose}
      title={t('tickets.form.title')}
      onSubmit={handleSubmit(onSubmit)}
      submitLabel={t('common:app.create')}
      cancelLabel={t('common:app.cancel')}
      isLoading={createMutation.isPending}
      maxWidth="sm"
    >
      <div className="space-y-4">
        <Controller
          name="subject"
          control={control}
          rules={{ required: t('tickets.errors.subjectRequired') }}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="subject">{t('tickets.subject')} *</Label>
              <Input
                id="subject"
                {...field}
                placeholder={t('tickets.form.subjectPlaceholder')}
                className={cn(errors.subject && 'border-destructive')}
              />
              {errors.subject && (
                <p className="text-sm text-destructive">{errors.subject.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="contactId"
          control={control}
          rules={{ required: t('tickets.errors.contactRequired') }}
          render={() => (
            <div className="space-y-2">
              <OrganizationContactComboSelect
                organizationId={organizationId}
                contactId={contactId}
                onCompanyChange={(val) => setValue('organizationId', val)}
                onContactChange={(val) => setValue('contactId', val)}
                contactRequired
              />
              {errors.contactId && (
                <p className="text-sm text-destructive">{errors.contactId.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="priority"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label htmlFor="priority">{t('tickets.priority')}</Label>
              <Select value={field.value} onValueChange={(v) => field.onChange(v as TicketPriority)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">{t('tickets.priorities.critical')}</SelectItem>
                  <SelectItem value="high">{t('tickets.priorities.high')}</SelectItem>
                  <SelectItem value="medium">{t('tickets.priorities.medium')}</SelectItem>
                  <SelectItem value="low">{t('tickets.priorities.low')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        />

        <Controller
          name="productId"
          control={control}
          render={() => (
            <ProductReferencePicker
              value={productReferenceFromFields(productId)}
              onChange={(ref) => setValue('productId', ref?.product_id ?? '')}
              productOnly
              productLabel={t('tickets.product')}
              productStatusFilter={['active']}
            />
          )}
        />

        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t('tickets.description')}</Label>
              <CompactRichTextEditor
                value={field.value}
                onChange={field.onChange}
                placeholder={t('tickets.form.descriptionPlaceholder')}
                minHeight={80}
              />
            </div>
          )}
        />

        <p className="text-xs text-muted-foreground">
          {t('tickets.quickCreateHint')}
        </p>
      </div>
    </DialogForm>
  );
}
